import {
  collection,
  doc,
  DocumentSnapshot,
  getDoc,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { useFirstRender, useWindowSize } from 'hooks';
import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { NavLink, useHistory, useParams } from 'react-router-dom';
import { capitalize } from 'utilities';
import { FetchState } from 'types';
import { Status } from 'shared/enums';
import { StatusLabel } from 'shared/Components/StatusLabel';
import { DashboardRoutePath } from 'modules/dashboard/navigation';

import { firestore, getArtistBalance, getPuzzlesByUser } from '../../../../services';
import { breakpointSizes } from '../../../../shared/styling';
import { Button, Card } from '../../../../shared/Components';
import { Box, FlexColumn, FlexContainer, Image, Text } from '../../../../shared/Typhography';
import { strings } from '../../../../strings';
import { ViewPuzzleScreen } from '../ViewPuzzleScreen';

interface ParamTypes {
  id: string;
}

export default function ViewArtistScreen() {
  const { id } = useParams<ParamTypes>();
  const isFirstRender = useFirstRender();
  const { size: windowSize } = useWindowSize();
  const history = useHistory();

  const { data: puzzles, isLoading: isLoadingPuzzles } = getPuzzlesByUser(id);

  const [artist, setArtist] = useState<DocumentSnapshot>();
  const [isLoadingArtist, setLoadingArtist] = useState<boolean>();
  const [following, setFollowing] = useState<number>(0);
  const [artistBalance, setArtistBalance] = useState<number | null>(null);
  const [artistBalanceFetchState, setArtistBalanceFetchState] = useState<FetchState>('pending');

  const fetchArtist = async () => {
    setLoadingArtist(true);
    const docRef = doc(firestore, `usersWeb/${id}`);
    const docData = await getDoc(docRef);
    setArtist(docData);
    setLoadingArtist(false);
  };

  const fetchFollowing = async () => {
    const followsRef = query(
      collection(firestore, 'follows'),
      where('followerId', '==', id),
      where('isFollowing', '==', true),
    );

    const docs = await getDocs(followsRef);
    setFollowing(docs.size);
  };

  const artistRequiresInitialApproval = artist?.data()?.requiresInitialApproval;

  /**
   * Calculate artists balance by subtracting total revenue from total payouts.
   * This implementation should be removed when we re-introduce `balance` field in artist document.
   */
  const calculateArtistBalance = async () => {
    try {
      setArtistBalanceFetchState('loading');
      const balance = await getArtistBalance(id);
      console.log('#1', balance);
      setArtistBalance(balance);
      setArtistBalanceFetchState('success');
    } catch (error: any) {
      setArtistBalanceFetchState('error');
      toast.error(error.message);
    }
  };

  React.useEffect(() => {
    fetchArtist();
  }, [id]);

  React.useEffect(() => {
    if (isFirstRender) fetchFollowing();
  }, [isFirstRender, id]);

  const artPuzzles = useMemo(
    () => puzzles?.docs?.sort((a, b) => b.data().plays - a.data().plays) ?? [],
    [puzzles],
  );

  return (
    <>
      {artistRequiresInitialApproval && (
        <Box margin="150px auto 25px" style={{ position: 'relative' }}>
          {puzzles?.docs?.map((puzzle) => (
            <ViewPuzzleScreen
              key={puzzle.id}
              puzzleData={{
                uuid: puzzle.id,
                id: puzzle.id,
                ...puzzle.data(),
              }}
              newArtistPortalMode={true}
            />
          ))}
        </Box>
      )}
      <Box
        margin={artistRequiresInitialApproval ? '25px auto' : '150px auto 50px'}
        style={{ position: 'relative' }}
      >
        {!isLoadingArtist && (
          <FlexContainer flexWrap={windowSize < breakpointSizes.md ? 'wrap' : 'nowrap'}>
            <Image
              cursor="pointer"
              src={artist?.data()?.profileImageUrl}
              borderRadiusType="circle"
              size="insightsThumbnail"
              alt={'Profile image'}
              margin={{ right: '80px' }}
              style={{ lineHeight: '130px', verticalAlign: 'center' }} // Centers image's alt text.
            />
            <FlexColumn alignItems="flex-start">
              <Text size={2.5} margin="0 0 5px 0">
                {artist?.data()?.Name}
              </Text>
              <FlexContainer justifyContent="space-between" gap="20px" margin="0 0 10px 0">
                <Text size={1} color="#7B828A">
                  {artist?.data()?.username ? `@${artist?.data()?.username}` : ''}
                </Text>
                <Text size={1} color="#7B828A">
                  {artist?.data()?.profileLink
                    ? `${artist?.data()?.profileLink}`
                    : 'No Profile Link'}
                </Text>
                <div> {/* TODO Reserved for buttons */} </div>
              </FlexContainer>
              <FlexContainer justifyContent="space-between" flexWrap="wrap" margin="0 0 10px 0">
                <Text size={1} color="#7B828A" margin="2px 40px 2px 0">
                  <span style={{ color: '#000' }}>{puzzles?.docs?.length ?? 0}</span>{' '}
                  {strings.images}
                </Text>
                <Text size={1} color="#7B828A" margin="2px 40px 2px 0">
                  <span style={{ color: '#000' }}>{artist?.data()?.Followers ?? 0}</span>{' '}
                  {strings.followers}
                </Text>

                {/* TODO Check if we are going to keep this field */}
                <Text size={1} color="#7B828A" margin="2px 40px 2px 0">
                  <span style={{ color: '#000' }}>{following ?? 0}</span> {strings?.following}
                </Text>
              </FlexContainer>
              <Image
                cursor="pointer"
                src={artist?.data()?.coverImageUrl}
                alt="Cover image"
                maxWidth="100%"
                mobWidth="auto"
              />
            </FlexColumn>
          </FlexContainer>
        )}
      </Box>
      <Box margin="25px auto">
        <FlexContainer flexDirection="column" alignItems="flex-start" justifyContent="space-evenly">
          <Text size={1} margin="15px 40px 2px 0" weight="bold">
            {'Email: '}
            <span style={{ color: 'black', fontWeight: 'normal' }}>
              {artist?.data()?.email || 'Email is not provided'}
            </span>
          </Text>

          <Text size={1} margin="15px 40px 2px 0" weight="bold">
            {'Role: '}
            <span style={{ color: 'black', fontWeight: 'normal' }}>
              {artist?.data()?.Role || 'Role is not provided'}
            </span>
          </Text>

          {artistRequiresInitialApproval && artist?.data()?.status && (
            <FlexContainer justifyContent="flex-start" alignItems="center">
              <Text size={1} margin="15px 10px 2px 0" weight="bold">
                {'Status: '}
              </Text>
              <StatusLabel
                value={Status[capitalize(artist?.data()?.status) as keyof typeof Status]}
              />
            </FlexContainer>
          )}

          <Text margin="15px 40px 2px 0" weight="bold">
            {'Country: '}
            <span style={{ color: 'black', fontWeight: 'normal' }}>
              {artist?.data()?.Country || 'Country is not provided'}
            </span>
          </Text>

          <Text margin="15px 40px 2px 0" weight="bold">
            {'City: '}
            <span style={{ color: 'black', fontWeight: 'normal' }}>
              {artist?.data()?.City || 'City is not provided'}
            </span>
          </Text>

          <Text margin="15px 40px 2px 0" weight="bold">
            {'Description: '}
            <span style={{ color: 'black', fontWeight: 'normal', wordBreak: 'break-all' }}>
              {artist?.data()?.Description || 'Description is not provided'}
            </span>
          </Text>

          <Text margin="15px 40px 2px 0" weight="bold">
            {'Created at: '}
            <span style={{ color: 'black', fontWeight: 'normal' }}>
              {artist?.data()?.createdAt?.toDate().toDateString()}
            </span>
          </Text>

          <Text margin="15px 40px 2px 0" weight="bold">
            {'Uid: '}
            <span style={{ color: 'black', fontWeight: 'normal' }}>{artist?.id}</span>
          </Text>

          <Text margin="15px 40px 2px 0" weight="bold">
            {'Portfolio url: '}
            <a href={artist?.data()?.portfolioUrl} target="_blank" rel="noreferrer">
              {artist?.data()?.portfolioUrl}
            </a>
          </Text>

          <Text margin="15px 40px 2px 0" weight="bold">
            {`Balance: ${artistBalance ? artistBalance.toFixed(2) + '$' : ''}`}

            <Button
              onClick={calculateArtistBalance}
              variant="white"
              disabled={artistBalanceFetchState === 'loading'}
              style={{ fontSize: '16px' }}
            >
              {/* Show calculate balance on first page load - when balance wasn't already fetched */}
              {!artistBalance &&
                artistBalanceFetchState !== 'loading' &&
                'Click to calculate balance'}
              {/* Show re-calculate when balance was already fetched */}
              {artistBalance &&
                artistBalanceFetchState !== 'loading' &&
                'Click to re-calculate balance'}
              {artistBalanceFetchState === 'loading' && 'Loading...'}
            </Button>
          </Text>
          <Text margin="15px 40px 2px 0" weight="bold">
            {'Payment Method: '}
            <span style={{ color: 'black', fontWeight: 'normal' }}>
              {artist?.data()?.paymentMethod}
            </span>
          </Text>
          <Text margin="15px 40px 2px 0" weight="bold">
            {'Payment Method Email: '}
            <span style={{ color: 'black', fontWeight: 'normal' }}>
              {artist?.data()?.paymentMethodEmail}
            </span>
          </Text>
          <Text margin="15px 40px 2px 0" weight="bold">
            {'Rejection reason: '}
            <span style={{ color: 'black', fontWeight: 'normal' }}>
              {artist?.data()?.rejectMessage}
            </span>
          </Text>
        </FlexContainer>
      </Box>
      {!artistRequiresInitialApproval && (
        <Box margin="25px auto">
          {!isLoadingArtist && artist?.data()?.description && (
            <div
              style={{ paddingBottom: '10px', borderBottom: 'solid 1px rgb(180 180 180 / 20%);' }}
            >
              <Text size={1.5}>{strings.about}</Text>
              <Text size={1} padding="10px 0">
                {artist?.data()?.description}
              </Text>
            </div>
          )}
          {!isLoadingPuzzles && (
            <>
              <Text size={1.5}>{strings.artPortfolio}</Text>
              {artPuzzles.length > 0 ? (
                <>
                  <FlexContainer flexWrap="wrap" justifyContent="space-between">
                    {artPuzzles.map((item) => {
                      const puzzle = item.data();

                      return (
                        <NavLink
                          style={{ textDecoration: 'none' }}
                          key={puzzle.collectionIdFirebase}
                          to={`/${DashboardRoutePath.VIEW_PUZZLE}/${item.id}`}
                        >
                          <Card
                            title={puzzle.name}
                            likes={puzzle.likes}
                            play={puzzle.plays}
                            imageUrl={puzzle?.iconURL}
                          />
                        </NavLink>
                      );
                    })}
                  </FlexContainer>
                </>
              ) : (
                <>
                  <Text size={1} padding="10px 0">
                    {strings.noArtsYet}
                  </Text>
                </>
              )}
            </>
          )}
        </Box>
      )}
      {artistRequiresInitialApproval && (
        <Box margin="25px auto">
          <FlexContainer>
            <Button
              width={200}
              height={50}
              //TODO: Replace route with const from routes.
              onClick={() => history.push(`/first-approval/${id}`)}
            >
              Start review process
            </Button>
          </FlexContainer>
        </Box>
      )}
    </>
  );
}
